import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from './config';
import { Box, Typography, Container, AppBar, Toolbar, Tabs, Tab, IconButton, Menu, MenuItem, Link, Button } from '@mui/material';
import { AccountCircle, ExitToApp, Settings } from '@mui/icons-material';
import SwaggerViewer from './components/SwaggerViewer';
import Documentation from './components/Documentation';
import AdminPanel from './components/AdminPanel';
import AboutUs from './components/AboutUs';
import ContactUs from './components/ContactUs';
import Login, { ResetPassword } from './components/Login';
import UserProfile from './components/UserProfile';

// Attach the stored JWT (if any) to every axios request so admin/write endpoints are authenticated
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function Home({ setTab }) {
  const heroImg = process.env.PUBLIC_URL + '/md-slider-image/slide-bg_3.jpg';

  const features = [
    { 
      subtitle: 'PaaS', 
      title: 'MIM Platform as a Service', 
      desc: 'MiM is a Platform as a Service (PaaS) and is built from the ground up using Infrastructure as a Service (IaaS) to allow rapid deployment. Our goal is to allow rapid development to all Marmon companies who want to explore IoT to bring another dimension to their products.',
      color: '#1976d2',
      icon: '🚀'
    },
    { 
      subtitle: 'BI', 
      title: 'Business Intelligence', 
      desc: 'Many IT, business intelligence and analytics managers have stopped asking, "What is the Internet of Things?" Instead, they\'ve started asking, "What is the business value of the data generated by the IoT? And what do we need to do to realize that value?"',
      color: '#9c27b0',
      icon: '📊'
    },
    { 
      subtitle: 'FM', 
      title: 'Fleet Management', 
      desc: 'OEM offers a fleet management which allows your manufacturing admin to do stuffs. IoT\'s role in fleet management is pretty straightforward. It all starts with sensors and devices embedded in the vehicles and the data they are capturing. Depending on the type of data a fleet manager wants to analyze, they would need to equip their trucks with the appropriate hardware sensors.',
      color: '#f57c00',
      icon: '🚗'
    },
    { 
      subtitle: 'OTA', 
      title: 'Real Time updates from device', 
      desc: 'MIM supports real-time updates to and from the device. This includes an on-demand oscilloscope to look at sensor data in real-time when troubleshooting and OTA updates of both firmware and media files.',
      color: '#43a047',
      icon: '📡'
    },
    { 
      subtitle: 'AC', 
      title: 'Auto commissioning & Just in time provisioning', 
      desc: 'MIM has an API to support the commissioning of the devices on the factory floor. Each unit gets programmed with its certificates and policies to allow fleet management control down to the unit level.',
      color: '#e91e63',
      icon: '⚙️'
    },
    { 
      subtitle: 'Dashboard', 
      title: 'Auto-generated Dashboard', 
      desc: 'The dashboard offers an automatically generated webpage for your end customer allowing your team to exchange information and demo your device before a front end web page is available. This early interaction allows the refining of the model and required critical data to power business front ends.',
      color: '#00acc1',
      icon: '📈'
    },
    { 
      subtitle: 'Ops', 
      title: 'DevOps Support', 
      desc: 'A dedicated DevOps team, which are experts in the area IoT. The team monitors and maintains the health of development, field test and production PaaS. The team ensures that the latest managed technologies are brought into the system to allow new and exciting feature sets like AI monitoring of attack vectors and device performance.',
      color: '#5e35b1',
      icon: '🛠️'
    },
    { 
      subtitle: 'MDL', 
      title: 'Modeling', 
      desc: 'The MiM platform allows your device and embedded developer to work together to define and maintain a model for your IoT data. MiM can then provide code snippets with all the critical information so it can be copied and pasted into your device to reduce any errors and time to market.',
      color: '#d32f2f',
      icon: '🔧'
    },
  ];

  return (
    <Box sx={{ p: 0, width: '100%', background: '#fff' }}>
      {/* Hero Section */}
      <Box sx={{ position: 'relative', minHeight: { xs: 500, md: 600 }, overflow: 'hidden' }}>
        <img src={heroImg} alt="Hero" style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0 }} />
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          background: 'linear-gradient(135deg, rgba(25, 30, 50, 0.85) 0%, rgba(25, 30, 50, 0.65) 100%)',
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          textAlign: 'center', 
          color: '#fff', 
          zIndex: 2,
          px: 2
        }}>
          <Box sx={{ 
            animation: 'fadeInUp 0.8s ease-out',
            '@keyframes fadeInUp': {
              from: { opacity: 0, transform: 'translateY(30px)' },
              to: { opacity: 1, transform: 'translateY(0)' }
            }
          }}>
            <img 
              src={process.env.PUBLIC_URL + '/marmon-logo.png'} 
              alt="Logo" 
              style={{ 
                height: 90, 
                marginBottom: 32, 
                background: '#fff', 
                borderRadius: 12, 
                padding: 12,
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
              }} 
            />
            <Typography 
              variant="h1" 
              sx={{ 
                fontWeight: 800, 
                mb: 3, 
                maxWidth: 900,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                lineHeight: 1.2,
                textShadow: '0 4px 12px rgba(0,0,0,0.3)'
              }}
            >
              MiM is incredibly secured IoT platform to connect & monitor your devices.
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 4, 
                maxWidth: 700,
                fontSize: { xs: '1.1rem', md: '1.4rem' },
                fontWeight: 300,
                opacity: 0.95,
                mx: 'auto'
              }}
            >
              The fastest way to grow your business with the leader in IoT Technology.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
              <a href="https://oem.marmonim.com/register" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                <Box 
                  component="span" 
                  sx={{ 
                    bgcolor: '#1976d2', 
                    color: '#fff', 
                    px: 5, 
                    py: 2, 
                    borderRadius: 3, 
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    cursor: 'pointer', 
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 20px rgba(25, 118, 210, 0.4)',
                    display: 'inline-block',
                    '&:hover': { 
                      bgcolor: '#115293',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 6px 30px rgba(25, 118, 210, 0.5)'
                    } 
                  }}
                >
                  Get Started Now
                </Box>
              </a>
              <Box 
                component="span" 
                onClick={() => setTab(3)}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.15)', 
                  backdropFilter: 'blur(10px)',
                  color: '#fff', 
                  px: 5, 
                  py: 2, 
                  borderRadius: 3, 
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  border: '2px solid rgba(255, 255, 255, 0.4)', 
                  cursor: 'pointer', 
                  transition: 'all 0.3s ease',
                    display: 'inline-block',
                    '&:hover': { 
                      bgcolor: 'rgba(255, 255, 255, 0.25)',
                      borderColor: '#fff',
                      transform: 'translateY(-3px)'
                    } 
                  }}
                >
                  Learn More
                </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* How & Where We Work Section */}
      <Box sx={{ 
        py: 6, 
        background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 50%, #fff3e0 100%)', 
        textAlign: 'center',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3
        }
      }}>
        <Container>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 800, 
              mb: 2, 
              color: '#1a1a2e',
              fontSize: { xs: '2rem', md: '2.5rem' },
              position: 'relative',
              zIndex: 1
            }}
          >
            How & Where we Work
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#666', 
              mb: 5, 
              maxWidth: 600, 
              mx: 'auto',
              position: 'relative',
              zIndex: 1
            }}
          >
            Our expertise spans across three critical domains of modern technology
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: { xs: 4, md: 8 }, 
            flexWrap: 'wrap', 
            px: 2,
            position: 'relative',
            zIndex: 1
          }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)'
              }
            }}>
              <Box sx={{ 
                width: 140, 
                height: 140, 
                borderRadius: '50%', 
                border: '4px solid #1976d2',
                background: 'linear-gradient(135deg, #1976d215 0%, #1976d235 100%)',
                overflow: 'hidden',
                position: 'relative',
                marginBottom: 2,
                boxShadow: '0 4px 20px rgba(25, 118, 210, 0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 32px rgba(25, 118, 210, 0.3)',
                  transform: 'scale(1.05)'
                }
              }}>
                <img 
                  src={process.env.PUBLIC_URL + '/inline-images/iot_team-xs.png'} 
                  alt="IoT" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover'
                  }} 
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:3rem;">🌐</div>';
                  }} 
                />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2', mb: 0.5 }}>IOT</Typography>
              <Typography variant="body2" sx={{ color: '#666', maxWidth: 180, textAlign: 'center', fontSize: '0.85rem' }}>
                Connected devices and smart solutions
              </Typography>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)'
              }
            }}>
              <Box sx={{ 
                width: 140, 
                height: 140, 
                borderRadius: '50%', 
                border: '4px solid #43a047',
                background: 'linear-gradient(135deg, #43a04715 0%, #43a04735 100%)',
                overflow: 'hidden',
                position: 'relative',
                marginBottom: 2,
                boxShadow: '0 4px 20px rgba(67, 160, 71, 0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 32px rgba(67, 160, 71, 0.3)',
                  transform: 'scale(1.05)'
                }
              }}>
                <img 
                  src={process.env.PUBLIC_URL + '/inline-images/Security353.jpg'} 
                  alt="Security" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover'
                  }} 
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:3rem;">🔒</div>';
                  }} 
                />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#388e3c', mb: 0.5 }}>Security</Typography>
              <Typography variant="body2" sx={{ color: '#666', maxWidth: 180, textAlign: 'center', fontSize: '0.85rem' }}>
                Enterprise-grade security protocols
              </Typography>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)'
              }
            }}>
              <Box sx={{ 
                width: 140, 
                height: 140, 
                borderRadius: '50%', 
                border: '4px solid #ff9800',
                background: 'linear-gradient(135deg, #ff980015 0%, #ff980035 100%)',
                overflow: 'hidden',
                position: 'relative',
                marginBottom: 2,
                boxShadow: '0 4px 20px rgba(255, 152, 0, 0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 32px rgba(255, 152, 0, 0.3)',
                  transform: 'scale(1.05)'
                }
              }}>
                <img 
                  src={process.env.PUBLIC_URL + '/inline-images/devops-big.png'} 
                  alt="DevOps" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover'
                  }} 
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:3rem;">⚡</div>';
                  }} 
                />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#f57c00', mb: 0.5 }}>DevOps</Typography>
              <Typography variant="body2" sx={{ color: '#666', maxWidth: 180, textAlign: 'center', fontSize: '0.85rem' }}>
                Continuous deployment and monitoring
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Our Features Section - Vertical Layout */}
      <Box sx={{ py: 6, background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, textAlign: 'center', color: '#1a1a2e', fontSize: { xs: '2rem', md: '2.5rem' } }}>
            Our Features
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'center', color: '#666', mb: 5, maxWidth: 600, mx: 'auto' }}>
            Discover powerful features that make MiM the leading IoT platform for enterprise solutions
          </Typography>
          
          {/* Features displayed vertically */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {features.map((feature, index) => (
              <Box 
                key={index}
                sx={{ 
                  display: 'flex',
                  flexDirection: { xs: 'column', md: index % 2 === 0 ? 'row' : 'row-reverse' },
                  alignItems: 'center',
                  gap: 2.5,
                  p: 2.5,
                  borderRadius: 3,
                  background: '#fff',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  }
                }}
              >
                {/* Icon/Badge Section */}
                <Box sx={{ 
                  minWidth: { xs: '100%', md: 150 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Box sx={{
                    width: 70,
                    height: 70,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${feature.color}15 0%, ${feature.color}35 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem',
                    mb: 1.5,
                    border: `2px solid ${feature.color}`,
                    boxShadow: `0 4px 16px ${feature.color}30`
                  }}>
                    {feature.icon}
                  </Box>
                  <Typography 
                    variant="overline" 
                    sx={{ 
                      color: feature.color, 
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      letterSpacing: 1.2
                    }}
                  >
                    {feature.subtitle}
                  </Typography>
                </Box>

                {/* Content Section */}
                <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700, 
                      mb: 1.5, 
                      color: '#1a1a2e',
                      fontSize: { xs: '1.25rem', md: '1.4rem' }
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#555', 
                      lineHeight: 1.6,
                      fontSize: '0.95rem'
                    }}
                  >
                    {feature.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Security Section */}
      <Box sx={{ 
        py: 4, 
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #1976d2 100%)', 
        textAlign: 'center',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          right: '-10%',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.05)',
          zIndex: 0
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-30%',
          left: '-5%',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.05)',
          zIndex: 0
        }
      }}>
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ 
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '2rem',
            mb: 2,
            mx: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            🔐
          </Box>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              mb: 2, 
              fontSize: { xs: '1.5rem', md: '2rem' },
              textShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}
          >
            All traffic to and from MiM is secure
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: '0.95rem',
              lineHeight: 1.6, 
              maxWidth: 700, 
              mx: 'auto',
              opacity: 0.95,
              mb: 2
            }}
          >
            All traffic to and from MiM is sent securely over TLS using X.509 certificates. 
            MIM stores your data in the most secured way using industry standard AES-256 
            encryption algorithm to encrypt your data on the server.
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: 2, 
            flexWrap: 'wrap',
            mt: 2
          }}>
            <Box sx={{ 
              textAlign: 'center',
              p: 2,
              borderRadius: 2,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              minWidth: 120,
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.15)',
                transform: 'translateY(-3px)'
              }
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1rem' }}>TLS</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>Encryption</Typography>
            </Box>
            <Box sx={{ 
              textAlign: 'center',
              p: 2,
              borderRadius: 2,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              minWidth: 120,
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.15)',
                transform: 'translateY(-3px)'
              }
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1rem' }}>X.509</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>Certificates</Typography>
            </Box>
            <Box sx={{ 
              textAlign: 'center',
              p: 2,
              borderRadius: 2,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              minWidth: 120,
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.15)',
                transform: 'translateY(-3px)'
              }
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1rem' }}>AES-256</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>Data Storage</Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Clients Section */}
      <Box sx={{ py: 4, background: '#fff', textAlign: 'center' }}>
        <Container>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              mb: 1.5, 
              color: '#1a1a2e',
              fontSize: { xs: '1.5rem', md: '2rem' }
            }}
          >
            We are happy to serve our customers!
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#1976d2', 
              mb: 3,
              fontWeight: 600,
              fontSize: { xs: '1rem', md: '1.2rem' }
            }}
          >
            We have 25,000+ connected devices across globe using MiM.
          </Typography>
          
          {/* Stats Cards */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
            gap: 2,
            mb: 2.5,
            maxWidth: 700,
            mx: 'auto'
          }}>
            <Box sx={{ 
              p: 2, 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1976d215 0%, #1976d235 100%)',
              border: '2px solid #1976d2',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 6px 20px rgba(25, 118, 210, 0.2)'
              }
            }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2', mb: 0.5 }}>25K+</Typography>
              <Typography variant="caption" sx={{ color: '#555', fontSize: '0.8rem' }}>Connected Devices</Typography>
            </Box>
            <Box sx={{ 
              p: 2, 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #43a04715 0%, #43a04735 100%)',
              border: '2px solid #43a047',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 6px 20px rgba(67, 160, 71, 0.2)'
              }
            }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#388e3c', mb: 0.5 }}>99.9%</Typography>
              <Typography variant="caption" sx={{ color: '#555', fontSize: '0.8rem' }}>Uptime</Typography>
            </Box>
            <Box sx={{ 
              p: 2, 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #ff980015 0%, #ff980035 100%)',
              border: '2px solid #ff9800',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 6px 20px rgba(255, 152, 0, 0.2)'
              }
            }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#f57c00', mb: 0.5 }}>24/7</Typography>
              <Typography variant="caption" sx={{ color: '#555', fontSize: '0.8rem' }}>Support</Typography>
            </Box>
          </Box>
          
          {/* Client Logos */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#666', fontSize: '0.85rem' }}>
            Trusted by leading companies
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            justifyContent: 'center', 
            gap: 4,
            alignItems: 'center',
            transition: 'all 0.3s ease'
          }}>
            <img src={process.env.PUBLIC_URL + '/inline-images/dms.png'} alt="DMS" style={{ height: 45, objectFit: 'contain', transition: 'all 0.3s ease' }} onError={(e) => (e.target.style.display = 'none')} />
            <img src={process.env.PUBLIC_URL + '/inline-images/apo.png'} alt="APO" style={{ height: 45, objectFit: 'contain', transition: 'all 0.3s ease' }} onError={(e) => (e.target.style.display = 'none')} />
            <img src={process.env.PUBLIC_URL + '/inline-images/eco.png'} alt="ECO" style={{ height: 45, objectFit: 'contain', transition: 'all 0.3s ease' }} onError={(e) => (e.target.style.display = 'none')} />
            <img src={process.env.PUBLIC_URL + '/inline-images/mft.png'} alt="MFT" style={{ height: 45, objectFit: 'contain', transition: 'all 0.3s ease' }} onError={(e) => (e.target.style.display = 'none')} />
            <img src={process.env.PUBLIC_URL + '/inline-images/miw.png'} alt="MIW" style={{ height: 45, objectFit: 'contain', transition: 'all 0.3s ease' }} onError={(e) => (e.target.style.display = 'none')} />
            <img src={process.env.PUBLIC_URL + '/inline-images/rail.png'} alt="Rail" style={{ height: 45, objectFit: 'contain', transition: 'all 0.3s ease' }} onError={(e) => (e.target.style.display = 'none')} />
          </Box>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box sx={{ 
        py: 3, 
        background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
        position: 'relative'
      }}>
        <Container>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              mb: 1, 
              textAlign: 'center', 
              color: '#1a1a2e',
              fontSize: { xs: '1.3rem', md: '1.6rem' }
            }}
          >
            Contact Us
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              textAlign: 'center', 
              color: '#666', 
              mb: 2.5,
              maxWidth: 550,
              mx: 'auto',
              fontSize: '0.85rem',
              display: 'block'
            }}
          >
            Get in touch with our team for support, inquiries, or partnership opportunities
          </Typography>
          
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            maxWidth: 650,
            mx: 'auto'
          }}>
            {/* India Contact */}
            <Box sx={{ 
              p: 2.5, 
              borderRadius: 2,
              background: '#fff',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
              }
            }}>
              <Box sx={{ 
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ff9933 0%, #138808 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.2rem',
                mb: 1.5,
                boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
              }}>
                🇮🇳
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#1a1a2e', fontSize: '1rem' }}>
                India Contact
              </Typography>
              <Typography variant="caption" sx={{ color: '#555', lineHeight: 1.5, fontSize: '0.8rem', display: 'block' }}>
                Marmon Technologies India Pvt Ltd<br />
                Brigade Senate, 2, Hebbal Kempapura<br />
                Bengaluru, Karnataka 560024
              </Typography>
            </Box>
            
            {/* US Contact */}
            <Box sx={{ 
              p: 2.5, 
              borderRadius: 2,
              background: '#fff',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
              }
            }}>
              <Box sx={{ 
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #b22234 0%, #3c3b6e 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.2rem',
                mb: 1.5,
                boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
              }}>
                🇺🇸
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#1a1a2e', fontSize: '1rem' }}>
                US Contact
              </Typography>
              <Typography variant="caption" sx={{ color: '#555', lineHeight: 1.5, mb: 1, fontSize: '0.8rem', display: 'block' }}>
                965 W Chicago Ave<br />
                Chicago, IL 60642
              </Typography>
              <Typography variant="caption" sx={{ color: '#555', fontSize: '0.8rem', display: 'block' }}>
                Email: <a 
                  href="mailto:mim-devops@marmon.com" 
                  style={{ 
                    color: '#1976d2', 
                    textDecoration: 'none',
                    fontWeight: 600,
                    transition: 'all 0.2s ease'
                  }}
                >
                  mim-devops@marmon.com
                </a>
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

function App() {
  // Initialize tab from localStorage, default to 0 if not found
  const [tab, setTab] = useState(() => {
    const savedTab = localStorage.getItem('activeTab');
    return savedTab ? parseInt(savedTab, 10) : 0;
  });
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);

  // Check authentication on mount
  useEffect(() => {
    checkAuthentication();
  }, []);

  // Restore hash if it was captured early but lost during navigation
  useEffect(() => {
    const initialHash = sessionStorage.getItem('initialHash');
    const initialSearch = sessionStorage.getItem('initialSearch');
    
    // If we have captured values but they're not in the current URL, restore them
    if (initialHash && !window.location.hash) {
      console.log('Restoring lost hash from early capture:', initialHash);
      window.location.hash = initialHash;
    }
    
    if (initialSearch && !window.location.search) {
      console.log('Restoring lost search params from early capture:', initialSearch);
      // We'll handle this in the login flow, just log for now
    }
  }, []);

  // Handle URL query parameters when already authenticated
  useEffect(() => {
    if (isAuthenticated && !isCheckingAuth) {
      const handleUrlChange = () => {
        console.log('handleUrlChange triggered');
        const queryParams = new URLSearchParams(window.location.search);
        const tabParam = queryParams.get('tab');
        const docParam = queryParams.get('doc');
        const swaggerParam = queryParams.get('swagger');
        const hash = window.location.hash;
        
        console.log('URL params - tab:', tabParam, 'doc:', docParam, 'swagger:', swaggerParam, 'hash:', hash);
        
        // If there's only a hash (deep link), assume Swagger tab
        if (hash && !tabParam && !docParam && !swaggerParam) {
          console.log('Hash-only URL detected, setting tab to 1');
          localStorage.setItem('activeTab', '1');
          setTab(1);
          // Don't clear the hash - let it stay for SwaggerUI
          return;
        }
        
        if (tabParam) {
          const tabValue = parseInt(tabParam);
          console.log('Setting tab from URL param:', tabValue);
          localStorage.setItem('activeTab', tabValue.toString());
          setTab(tabValue);
        }
        
        if (docParam) {
          sessionStorage.setItem('openDocWithShareCode', docParam);
          console.log('Stored doc param in sessionStorage:', docParam);
        }
        
        if (swaggerParam) {
          sessionStorage.setItem('openSwaggerCategory', swaggerParam);
        }
        
        // Clear the URL parameters after processing
        // For Swagger, delay to ensure components pick up the hash
        // For Documentation, clear immediately since we use sessionStorage
        if (tabParam || docParam || swaggerParam) {
          const preserveHash = (tabParam === '1' || swaggerParam) ? hash : '';
          
          if (tabParam === '1' || swaggerParam) {
            // Swagger tab: delay to ensure SwaggerViewer processes the hash
            setTimeout(() => {
              window.history.replaceState({}, '', window.location.pathname + preserveHash);
            }, 500);
          } else {
            // Documentation or other tabs: clear immediately
            setTimeout(() => {
              window.history.replaceState({}, '', window.location.pathname + preserveHash);
            }, 100); // Short delay just to ensure sessionStorage is read first
          }
        }
      };
      
      // Run immediately
      handleUrlChange();
      
      // Listen for hash changes (when user manually changes URL)
      window.addEventListener('hashchange', handleUrlChange);
      
      return () => {
        window.removeEventListener('hashchange', handleUrlChange);
      };
    }
  }, [isAuthenticated, isCheckingAuth]);

  // Clear URL hash when switching away from Swagger tab
  useEffect(() => {
    if (tab !== 1 && window.location.hash) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [tab]);

  const checkAuthentication = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsCheckingAuth(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsAuthenticated(true);
      } else {
        // Token invalid, clear it
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const handleLoginSuccess = (userData) => {
    console.log('Login success called');
    setUser(userData);
    setIsAuthenticated(true);
    
    // Check if there were query parameters saved before login
    const savedQueryParams = sessionStorage.getItem('preLoginQueryParams');
    const savedHash = sessionStorage.getItem('preLoginHash');
    
    console.log('Saved query params:', savedQueryParams);
    console.log('Saved hash:', savedHash);
    
    let tabWasSet = false;
    
    if (savedQueryParams) {
      const params = new URLSearchParams(savedQueryParams);
      const tabParam = params.get('tab');
      const docParam = params.get('doc');
      const swaggerParam = params.get('swagger');
      
      console.log('Tab param:', tabParam, 'Doc param:', docParam, 'Swagger param:', swaggerParam);
      
      if (tabParam) {
        const tabValue = parseInt(tabParam);
        console.log('Setting tab to:', tabValue);
        localStorage.setItem('activeTab', tabValue.toString());
        setTab(tabValue);
        tabWasSet = true;
      }
      
      // Store docParam for Documentation component to pick up
      if (docParam) {
        sessionStorage.setItem('openDocWithShareCode', docParam);
        console.log('=== POST-LOGIN: Stored doc param in sessionStorage:', docParam);
      }
      
      // Store swaggerParam for SwaggerViewer component to pick up
      if (swaggerParam) {
        sessionStorage.setItem('openSwaggerCategory', swaggerParam);
      }
      
      // Clear the saved params
      sessionStorage.removeItem('preLoginQueryParams');
    }
    
    // Restore the hash after login
    if (savedHash) {
      // Ensure we're on Swagger tab if there's a hash and tab wasn't already set
      if (!tabWasSet) {
        console.log('Tab not set by params, forcing tab to 1 for hash');
        localStorage.setItem('activeTab', '1');
        setTab(1);
      }
      
      // CRITICAL: Don't set the hash directly! SwaggerUI will truncate it.
      // Instead, store it in pendingDeepLink and let SwaggerViewer handle it properly
      console.log('=== POST-LOGIN: Storing Swagger deep link for SwaggerViewer to restore:', savedHash);
      sessionStorage.setItem('pendingDeepLink', savedHash);
      
      // Give SwaggerViewer time to initialize and restore the hash properly
      // The SwaggerViewer will check pendingDeepLink and restore it correctly
      setTimeout(() => {
        // Check if SwaggerViewer hasn't restored it yet, trigger manually
        if (sessionStorage.getItem('pendingDeepLink') === savedHash) {
          console.log('=== POST-LOGIN: Triggering manual restoration');
          window.dispatchEvent(new Event('hashchange'));
        }
      }, 1000);
      
      sessionStorage.removeItem('preLoginHash');
    }
    
    console.log('handleLoginSuccess completed. Tab should be:', tabWasSet ? 'from params' : '1 (from hash)');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('activeTab');
    // Clear any saved login redirect data
    sessionStorage.removeItem('preLoginQueryParams');
    sessionStorage.removeItem('preLoginHash');
    sessionStorage.removeItem('openDocWithShareCode');
    sessionStorage.removeItem('openSwaggerCategory');
    setUser(null);
    setIsAuthenticated(false);
    setTab(0);
    // Clear the URL hash
    window.history.replaceState({}, '', window.location.pathname);
    handleMenuClose();
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Save tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('activeTab', tab.toString());
  }, [tab]);

  // Track page views with Google Analytics
  useEffect(() => {
    if (window.gtag) {
      const tabNames = ['Home', 'Swagger', 'Documentation', 'About Us', 'Contact Us', 'Admin', 'Profile'];
      const pageName = tabNames[tab] || 'Unknown';
      window.gtag('config', 'G-X4YE4781Z7', {
        page_path: `/${pageName.toLowerCase().replace(/\s+/g, '-')}`,
        page_title: `${pageName} - MARMONIM`
      });
    }
  }, [tab]);

  // Scroll to top when tab changes
  const handleTabChange = (newTab) => {
    setTab(newTab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Show loading while checking authentication
  if (isCheckingAuth) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // Check if user is on the reset password page
  const isResetPasswordPage = window.location.pathname === '/reset-password';
  if (isResetPasswordPage) {
    return <ResetPassword />;
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    // Save query parameters and hash before showing login
    // First check if we have early-captured values from index.html
    let queryParams = sessionStorage.getItem('initialSearch') || window.location.search;
    let hash = sessionStorage.getItem('initialHash') || window.location.hash;
    
    // Clean up early-captured values
    if (sessionStorage.getItem('initialSearch')) {
      sessionStorage.removeItem('initialSearch');
    }
    if (sessionStorage.getItem('initialHash')) {
      sessionStorage.removeItem('initialHash');
    }
    
    console.log('Not authenticated. Query:', queryParams, 'Hash:', hash);
    
    // Always save fresh params and hash (overwrite if exists)
    if (queryParams) {
      sessionStorage.setItem('preLoginQueryParams', queryParams);
      console.log('=== PRE-LOGIN: Saved query params:', queryParams);
    } else {
      // If no query params but we have a hash, assume Swagger deep link
      if (hash) {
        sessionStorage.setItem('preLoginQueryParams', '?tab=1');
        console.log('=== PRE-LOGIN: No query params but has hash. Setting tab=1');
      }
    }
    
    if (hash) {
      sessionStorage.setItem('preLoginHash', hash);
      console.log('=== PRE-LOGIN: Saved Swagger deep link hash for post-login restoration:', hash);
    }
    
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', background: '#f4f6f8', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" sx={{ bgcolor: '#fff', boxShadow: '0 2px 8px #00000015' }}>
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: 64, px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <img src={process.env.PUBLIC_URL + '/marmon-logo.png'} alt="Marmon Logo" style={{ height: 48 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0050b3', letterSpacing: 1 }}>MARMONIM</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tabs value={tab} onChange={(_, v) => handleTabChange(v)} textColor="primary" indicatorColor="primary">
              <Tab label="Home" sx={{ color: '#0050b3', fontWeight: 600, textTransform: 'none' }} />
              <Tab label="Swagger" sx={{ color: '#0050b3', fontWeight: 600, textTransform: 'none' }} />
              <Tab label="Documentation" sx={{ color: '#0050b3', fontWeight: 600, textTransform: 'none' }} />
              <Tab label="About Us" sx={{ color: '#0050b3', fontWeight: 600, textTransform: 'none' }} />
              <Tab label="Contact Us" sx={{ color: '#0050b3', fontWeight: 600, textTransform: 'none' }} />
              {user && user.role === 'admin' && (
                <Tab label="Admin" sx={{ color: '#0050b3', fontWeight: 600, textTransform: 'none' }} />
              )}
            </Tabs>
            <IconButton onClick={handleMenuOpen} sx={{ color: '#0050b3' }}>
              <AccountCircle />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 180,
                  borderRadius: 1
                }
              }}
            >
              <MenuItem onClick={() => { handleMenuClose(); handleTabChange(6); }}>
                <Settings sx={{ mr: 1, fontSize: 20 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ExitToApp sx={{ mr: 1, fontSize: 20 }} />
                Sign out
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, width: '100%' }}>
        {tab === 0 && <Home setTab={handleTabChange} />}
        {tab === 1 && <SwaggerViewer />}
        {tab === 2 && <Documentation />}
        {tab === 3 && <AboutUs />}
        {tab === 4 && <ContactUs />}
        {tab === 5 && user && user.role === 'admin' && <AdminPanel />}
        {tab === 6 && <UserProfile />}
      </Box>

      <Box component="footer" sx={{ bgcolor: '#1a2235', color: '#fff', py: 3, mt: 4 }}>
        <Container sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2">Copyright  2026 Marmon IM.</Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Link onClick={() => handleTabChange(3)} underline="hover" sx={{ color: '#fff', cursor: 'pointer' }}>About Us</Link>
            <Link onClick={() => handleTabChange(4)} underline="hover" sx={{ color: '#fff', cursor: 'pointer' }}>Contact Us</Link>
            <Link href="https://www.marmon.com/privacy_policy.html" target="_blank" rel="noopener" underline="hover" sx={{ color: '#fff', cursor: 'pointer' }}>Privacy Policy</Link>
            <Link href={process.env.PUBLIC_URL + '/EULA_Agreement_0.pdf'} target="_blank" rel="noopener" underline="hover" sx={{ color: '#fff', cursor: 'pointer' }}>EULA</Link>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default App;
